/**
 * Outcomes: from checks to one result per software and indicator, and how
 * they group.
 *
 * Turning checks into something that can be counted.
 *
 * The unit here is the indicator, not the check. An indicator is frequently
 * assessed by more than one tool, and in real reports those tools contradict
 * each other often. Counting checks would weight such indicators twice over and
 * bury the disagreement, so checks are first grouped by indicator and resolved
 * to a single state -- one of which is "they disagree".
 */

import { UNCLASSIFIED } from '../dimensions'
import type { VocabularyRegistry } from '../registry'
import type { Check, Consensus, DimensionRef, IndicatorOutcome, Report } from '../types'

/**
 * What identifies an indicator: its URI, or its slug where a report gave no
 * URI. One function, so every grouping and lookup agrees.
 */
export function indicatorKey(item: { indicatorUri: string; indicatorSlug: string }): string {
  return item.indicatorUri || item.indicatorSlug
}

/** A check together with the report it came from, needed for ordering. */
export interface SourcedCheck {
  check: Check
  reportId: string
  softwareName: string
  /** Identity, not display name: two projects may share a name. */
  softwareKey: string
  at?: number
}

export function sourcedChecks(reports: Report[]): SourcedCheck[] {
  return reports.flatMap((report) =>
    report.checks.map((check) => ({
      check,
      reportId: report.id,
      softwareName: report.software.name,
      softwareKey: report.software.key,
      at: report.createdAt?.value,
    })),
  )
}

/**
 * Keeps only the most recent check per software, indicator and tool.
 *
 * Without this, a session holding several assessments of the same project would
 * treat last month's result and this month's as two opinions and call the
 * difference a conflict, when it is simply progress. Conflict should mean two
 * tools disagreeing about the same moment.
 *
 * Reports with no readable date cannot be ordered, so they lose to any dated
 * report and are reported separately rather than being given an arbitrary
 * position.
 */
export function latestPerTool(entries: SourcedCheck[]): SourcedCheck[] {
  const best = new Map<string, SourcedCheck>()
  for (const entry of entries) {
    const key = `${entry.softwareKey}\u0000${entry.check.indicatorUri}\u0000${entry.check.tool.name}`
    const current = best.get(key)
    if (!current || isNewer(entry, current)) best.set(key, entry)
  }
  return [...best.values()]
}

function isNewer(candidate: SourcedCheck, incumbent: SourcedCheck): boolean {
  if (candidate.at === undefined) return false
  if (incumbent.at === undefined) return true
  return candidate.at > incumbent.at
}

/**
 * Resolves the checks for one indicator to a single state.
 *
 * A check whose output could not be interpreted does not vote: it cannot be
 * counted as agreement or as disagreement. If nothing votes, the result is
 * unknown, which keeps the indicator visible as a gap instead of quietly
 * vanishing from the totals.
 */
export function consensusOf(checks: Check[]): { consensus: Consensus; disputed: boolean } {
  let pass = 0
  let fail = 0
  let notApplicable = 0

  for (const check of checks) {
    if (check.verdict === 'pass') pass += 1
    else if (check.verdict === 'fail') fail += 1
    else if (check.verdict === 'notApplicable') notApplicable += 1
  }

  const disputed = pass > 0 && fail > 0

  if (pass > 0 && fail > 0) return { consensus: 'conflict', disputed }
  if (pass > 0) return { consensus: 'pass', disputed }
  if (fail > 0) return { consensus: 'fail', disputed }
  if (notApplicable > 0) return { consensus: 'notApplicable', disputed }
  return { consensus: 'unknown', disputed }
}

/** Indicator outcomes within a single report. */
export function outcomesFor(report: Report): IndicatorOutcome[] {
  return groupToOutcomes(
    report.checks.map((check) => ({ check })),
    { key: report.software.key, name: report.software.name },
  )
}

/**
 * Indicator outcomes across reports: for each piece of software, one per
 * indicator, from its latest result per tool.
 *
 * Grouped per software before anything is combined. Merging two projects'
 * checks into one outcome made a portfolio read as "conflict" wherever they
 * differed, and "passing" only where every one of them passed, so the more
 * software was loaded, the closer the passing count fell to zero.
 */
export function currentOutcomes(reports: Report[]): IndicatorOutcome[] {
  const bySoftware = new Map<string, SourcedCheck[]>()
  for (const entry of latestPerTool(sourcedChecks(reports))) {
    const bucket = bySoftware.get(entry.softwareKey)
    if (bucket) bucket.push(entry)
    else bySoftware.set(entry.softwareKey, [entry])
  }
  return [...bySoftware.entries()]
    .flatMap(([key, entries]) => groupToOutcomes(entries, { key, name: entries[0]!.softwareName }))
    .sort(
      (a, b) =>
        a.indicatorSlug.localeCompare(b.indicatorSlug) ||
        a.software.name.localeCompare(b.software.name),
    )
}

/** One outcome's identity: the indicator, and the software it is about. */
export function outcomeKey(outcome: IndicatorOutcome): string {
  return `${outcome.software.key}\u0000${indicatorKey(outcome)}`
}

function groupToOutcomes(
  entries: { check: Check }[],
  software: IndicatorOutcome['software'],
): IndicatorOutcome[] {
  const groups = new Map<string, Check[]>()
  for (const { check } of entries) {
    const key = indicatorKey(check)
    const bucket = groups.get(key)
    if (bucket) bucket.push(check)
    else groups.set(key, [check])
  }

  return [...groups.entries()]
    .map(([, checks]) => {
      const { consensus, disputed } = consensusOf(checks)
      const first = checks[0]!
      return {
        indicatorUri: first.indicatorUri,
        indicatorSlug: first.indicatorSlug,
        software,
        checks,
        consensus,
        disputed,
      }
    })
    .sort((a, b) => a.indicatorSlug.localeCompare(b.indicatorSlug))
}

export interface OutcomeCounts {
  pass: number
  fail: number
  conflict: number
  notApplicable: number
  unknown: number
}

export function countOutcomes(outcomes: IndicatorOutcome[]): OutcomeCounts {
  const counts: OutcomeCounts = { pass: 0, fail: 0, conflict: 0, notApplicable: 0, unknown: 0 }
  for (const outcome of outcomes) counts[outcome.consensus] += 1
  return counts
}

/** The pass rate in a sentence, wherever one is shown. */
export const PASS_RATE_DEFINITION =
  'Pass rate: passing results out of those that passed, failed or conflicted. Not applicable and unknown are left out.'

/**
 * Pass rate over indicators that actually reached a verdict.
 *
 * A conflict counts in the denominator but not the numerator, so disagreement
 * lowers the figure without being recorded as a failure. Indicators that did
 * not apply, or could not be interpreted, are excluded entirely and reported as
 * coverage instead -- folding them in either direction would misstate the
 * result.
 */
/** How many outcomes reached a verdict that counts towards the pass rate. */
export function ratedCount(counts: Pick<OutcomeCounts, 'pass' | 'fail' | 'conflict'>): number {
  return counts.pass + counts.fail + counts.conflict
}

export function passRate(counts: OutcomeCounts): number | undefined {
  const rated = ratedCount(counts)
  return rated === 0 ? undefined : counts.pass / rated
}

export interface DimensionStats {
  dimension: DimensionRef
  counts: OutcomeCounts
  /** Indicators that reached a verdict: pass, fail or conflict. */
  rated: number
  passRate: number | undefined
  outcomes: IndicatorOutcome[]
}

/**
 * Groups outcomes by quality dimension.
 *
 * An indicator belonging to several dimensions is counted in every one of them.
 * That is how the vocabulary defines them, so these groups overlap: the
 * per-dimension totals will add up to more than the number of indicators. The
 * interface has to state this, and must not draw dimensions as parts of a
 * whole, because they are not.
 */
export function byDimension(
  outcomes: IndicatorOutcome[],
  registry: VocabularyRegistry,
): DimensionStats[] {
  const buckets = new Map<string, { dimension: DimensionRef; outcomes: IndicatorOutcome[] }>()

  for (const outcome of outcomes) {
    const indicator = registry.get(outcome.indicatorUri)
    const dimensions = indicator.dimensions.length > 0 ? indicator.dimensions : [UNCLASSIFIED]
    for (const dimension of dimensions) {
      const bucket = buckets.get(dimension.slug)
      if (bucket) bucket.outcomes.push(outcome)
      else buckets.set(dimension.slug, { dimension, outcomes: [outcome] })
    }
  }

  return [...buckets.values()]
    .map(({ dimension, outcomes: grouped }) => {
      const counts = countOutcomes(grouped)
      return {
        dimension,
        counts,
        rated: ratedCount(counts),
        passRate: passRate(counts),
        outcomes: grouped,
      }
    })
    .sort((a, b) => a.dimension.name.localeCompare(b.dimension.name))
}
